import { subscribe, } from "@bracket/rabbit/mqtt";
import { isValidChainId, type UserAgreementType, type VaultIdentifier, } from "@bracket/types";
import { balanceOfWallet, bracketRate, latestPriceEth, } from "@cachables";
import * as G from "@globals";
import type { EventsControllerState, } from "@reown/appkit";
import { awaitTruthy, profile, } from "@store/helpers";
import type { IBracketStore, } from "@store/types";
import { wagmiConfig as config, modal, } from "@wagmiconfig";
import { produce } from 'immer';
import { getAccount, getBalance, watchAccount, } from "wagmi/actions";
import type { StateCreator, } from "zustand";	
import type { Store } from '../useStore';
import { WalletSliceDefaultprops, } from "./defaults";
import * as R from "./resolvers";
import type { ExtGetAccountReturnType, IWalletSlice, } from "./types";
import type { StoreSetter, StoreGetter, } from "@types";

export const createWalletSlice: StateCreator<IBracketStore, [], [], IWalletSlice> = ( set: StoreSetter, get: StoreGetter, ) => {


	const fetch = async ( force: boolean = false, ) => {

		profile( "wallet::fetch");

		if(!force && (get().wallet.ready || get().wallet.fetching)) {
			return;
		}

		set(produce<Store>(state => {
			state.wallet.fetching = true;
		}));

		_watch();

		const { status, address, chainId, } = getAccount(config);

		if(status === "disconnected" || status === "connecting" || status === "reconnecting") {
			set(produce<Store>(state => {
				state.wallet.fetching = false;
			}));
			return;
		}

		if(address && chainId && status === "connected") {
			await _consumeStableConnection();
		}

		if(!address) {
			await signInAnon();
			return;
		}

		// if(!_listening) {
		// 	_listening = true;
		// 	// modal.subscribeEvents(__onModalEvent);
		// }

		set(produce<Store>(state => {
			state.wallet.fetching = false;
		}));

	}

	const fetchBrktTransactions = async () => {
		const transactions = await R.getBrktTransactions(get());
		set(produce<Store>(state => {
			state.wallet.brkt_transactions = transactions;
		}));
	};

	const agree = async ( agreement: UserAgreementType, vault?: VaultIdentifier, ) => {

		profile( "wallet::agree", { info: [agreement, vault,].join(", ") });
		const { address, } = get().wallet;
		const vaultAddress = vault ? get().vaults.available.find(v => v.address === vault || v.symbol === vault)?.address : undefined;

		if(!address) {
			throw new Error("no address found");
		}
		if(hasAgreed(agreement, vault)) {
			console.warn("wallet::agree:: already agreed to", agreement, vault);
			return;
		}
		await R.agree(agreement, vaultAddress);
		await _refetchUser();

	}


	const claimEscrow = async () => {

		const { has_claim, claim_proof, claim_amount, user, address, } = get().wallet;

		if(!address) {
			throw new Error("no address found");	
		}

		if(has_claim) {

			try {

				set(produce<Store>(state => { state.wallet.claiming = true; }));
				const hash = await R.escrow.claim({ amount: claim_amount, proof: claim_proof, email: user?.email || "0x00@bracket.fi", address,  });
				if( hash ) {
		
					get().notificationSlice.addNotification({
						title: "BrktETH Claim",
						message: "Your claim was successful.",
						type: "success",
						status: "success",
						secondaryAction: {
							label: "Dismiss",
							onClick: () => console.log("Notification dismissed"),
						},
					});

					set(produce<Store>(state => {
						state.wallet.has_claim = false;
						state.wallet.claim_amount = BigInt(0);
						state.wallet.claim_proof = [];
						state.wallet.claiming = false;
					}));

				}
				else {
					throw new Error("failed to claim BrktETH");
				}
			}
			catch( err ) {

				console.error("Error claiming BrktETH", err);
				get().notificationSlice.addNotification({
					title: "BrktETH Claim",
					message: "Your claim was not successful. Please try again soon.",
					type: "error",
					status: "error",
				});
				set(produce<Store>(state => {
					state.wallet.claiming = false;
				}));

			}
		}
		else {
			get().notificationSlice.addNotification({
				title: "BrktETH Claim",
				message: "No claim available.",
				type: "error",
				status: "error",
			});
		}
	};


	const cleanup = () => {
		_clearPoll();
		if(walletSub) {
			walletSub();
		}
		if(watcher) {
			watcher();
		}
	};


	const connect = async (): Promise<void> => {
		modal.open({ view: "Connect", namespace: "eip155", });
		set(produce<Store>(state => {
			state.wallet.resetting = true;
		}));
		const { useStore, } = await import("@bracket/platform/store");
		await awaitTruthy(useStore, state => state.wallet.ready);
		await get().vaults.resetAsync();
	};


	const disconnect = async (): Promise<void> => {
		await modal.disconnect();
		await signInAnon();
		await get().vaults.resetAsync();
	};


	const hasAgreed = ( agreement: UserAgreementType, vault?: VaultIdentifier, ) => {
		profile( "wallet::hasAgreed");
		const { address, user, } = get().wallet;
		if(!address || !user) {
			throw new Error("@bracket/wallet::hasAgreed -- not in a stable state, no address or user found");
		}
		const agreements = user.agreements || [];
		if(vault) {
			const vaultInstance = get().vaults.available.find(v => v.address === vault || v.symbol === vault);
			if(!vaultInstance) {
				throw new Error(`@bracket/wallet::hasAgreed -- vault not found identified by "${vault}"`);
			}
			return agreements.find(agreed => agreed.agreement === agreement && !vault || vaultInstance.address === agreed.vaultAddress) || null;
		}
		return agreements.find(agreed => agreed.agreement === agreement) || null;
	}


	const signInAnon = async () => {
		profile( "wallet::signInAnon");
		set(produce<Store>(state => {
			state.wallet.fetching = false;
		}));
		await R.connectAnonClient();
	};


	const reset = () => {
		profile( "wallet::reset");
		_resetToDefaults();
	};


	const resetAsync = async () => {
		profile( "wallet::resetAsync");
		reset();
	};


	const _consumeStableConnection = async () => {

		profile( "wallet::_consumeStableConnection");

		const { address, } = getAccount(config);
		if(!address) {
			return;
		}

		const { VITE_ANON_WALLET_ADDR } = import.meta.env;
		const anonymous = address === VITE_ANON_WALLET_ADDR;

		set(produce<Store>(state => {
			state.wallet.address = address;
			state.wallet.anonymous = anonymous;
		}));

		G.wallet.setBracketWallet({ address, anonymous, });

		await _checkWalletIn();
		await _watchConnectedWallet();
		
		if(!_poll_listener) {
			_startPoll();
		}

		await fetchBrktTransactions();

		return;

	}


	const _checkWalletIn = async () => {

		const { address, anonymous, } = get().wallet;
		if(!address) {
			throw new Error("WalletSlice::checkWalletIn:: no address found");
		}

		const { address:wagmiAddress, isConnected, chainId, } = getAccount(config);

		if(!wagmiAddress || address.toLowerCase() !== wagmiAddress.toLowerCase()) {
			throw new Error("WalletSlice::checkWalletIn:: address mismatch");
		}

		let checked_in: Awaited<ReturnType<typeof R.checkin>> | null = null;
		let brkt_balance = BigInt(0);
		let eth_balance = { value: BigInt(0), };
		let wallet: Awaited<ReturnType<typeof R.userData>>;

		let brkt_conv_rate = 0;
		let eth_price = 0;
		let whitelisted = false;
		let claim_details: Awaited<ReturnType<typeof R.escrow.getClaimedAmount>> = {
			claimable: false,
			amount: "0",
			proof: [],
		};

		if(!anonymous) {
			[
				checked_in,
				brkt_balance,
				eth_balance,
				wallet,
				brkt_conv_rate,
				eth_price,
				whitelisted,
				claim_details,
			] = await Promise.all([
				R.checkin(address),
				balanceOfWallet({ token: { symbol: "BRKTETH", chainId: 1, }, }),
				getBalance(config, { address, }),
				R.userData(get()),
				bracketRate(),
				latestPriceEth(),
				R.walletIsWhiteList(),
				R.escrow.getClaimedAmount(address),
			]);

			R.TRM_screenAddress(address).then(pass => { set(produce<Store>(state => { state.wallet.trm_pass = pass; } ))});

		}
		else {
			wallet = await R.userData(get());
		}

		set(produce<Store>(state => {
			state.wallet.checked_in = Boolean(checked_in);
			state.wallet.brkt_balance = brkt_balance
			state.wallet.eth_balance = eth_balance.value;
			state.wallet.brkt_conv_rate = brkt_conv_rate;
			state.wallet.eth_price = eth_price;
			state.wallet.ready = true;
			state.wallet.user = wallet.user;
			state.wallet.isConnected = isConnected;
			state.wallet.isSupportedChain = isValidChainId(chainId);
			state.wallet.balance = {
				usd: (Number(brkt_balance)/1e18) * brkt_conv_rate * eth_price,
				brkt: brkt_balance.toString(),
			};
			state.wallet.whitelisted = whitelisted;
			state.wallet.has_claim = claim_details.claimable;
			state.wallet.claim_amount = BigInt(claim_details.amount);
			state.wallet.claim_proof = claim_details.proof;
			state.wallet.claimr_rank = wallet.claimr_rank;
			state.wallet.claimr_points = wallet.claimr_points;
			state.wallet.trm_pass = true;
		}));

	}


	const _refetchUser = async () => {
		const { user, } = await R.userData(get());
		set(produce<Store>(state => {
			state.wallet.user = user;
		}));
	};


	const _resetToDefaults = () => {
		Object.entries(WalletSliceDefaultprops).forEach(([key, value]) => {
			set(produce<Store>(state => {
				if(key in state.wallet) {
					// @ts-expect-error - we know this is safe...
					state.wallet[key] = value;
				} else {
					console.warn("store:wallet::resetToDefaults:: key", key, "not in state.wallet, yet we are setting it");
				}
			}));
		});
		G.wallet.setBracketWalletAnonymous();
	}


	const _startPoll = () => {
		_clearPoll();
		_poll_listener = setInterval(async () => {
			const { anonymous, } = get().wallet;
			const brkt_balance = anonymous ? BigInt(0) : await balanceOfWallet({ token: { symbol: "BRKTETH", chainId: 1, }, });
			if(brkt_balance !== get().wallet.brkt_balance) {
				set(produce<Store>(state => {
					state.wallet.brkt_balance = brkt_balance;
				}));
			}
		}, 10000);
	};


	const _clearPoll = () => {
		if(_poll_listener) {
			clearInterval(_poll_listener);
			_poll_listener = null;
		}
	};


	const __onAccountChange = async (C: Partial<ExtGetAccountReturnType>, _: Partial<ExtGetAccountReturnType> = {}, ): Promise<void> => {

		const { status, address, chain, chainId, } = C;
		const { address: currentAddress, isConnected: currentlyConnected, fetching, } = get().wallet;

		if(status === "reconnecting" || status === "connecting") {
			return;
		}

		if(status === "connected") {

			if(fetching || currentlyConnected && currentAddress === address) {
				return;
			}

			if(chain && isValidChainId(chainId)) {
				await _consumeStableConnection();
				if(currentAddress !== address) { // user has swtiched accounts.
					get().vaults.resetAsync();
				}
				return;
			}

			console.warn("store:wallet::onAccountChange:: invalid chain", chain, chainId);
			return;
		}

		if(status === "disconnected") {
			await resetAsync();
			signInAnon();
			return;
		}

	};


	const __onModalEvent = ( evt: EventsControllerState, ) => {
		profile( "wallet::__onModalEvent", { info: evt.data.event, });
		const { data, data: { event }, } = evt;


		switch(event) {

			case "INITIALIZE": {
				break;
			}

			case "DISCONNECT_SUCCESS": {
				// i believe this is more about reown disconnecting, not the wallet itself.
				const { address, } = get().wallet;
				const { address: wagmiAddress, } = getAccount(config);

				if(address !== wagmiAddress) {
					// console.log("DISCONNECT BUT ADDRESSES DIFFER (STORE, WAGMI):::>", address, wagmiAddress);
					if(wagmiAddress) {
						set(produce<Store>(state => {
							state.wallet.address = wagmiAddress;
						}));
					}
				}
				break;
			}

			case "SELECT_WALLET": {
				break;
			}

			case "CONNECT_SUCCESS": {
				if(!get().wallet.ready) {
					// console.log("WALLET >", event, JSON.parse(JSON.stringify(evt)),);
					( async () => {
						const { useStore, } = await import("@store");
						await awaitTruthy(useStore, state => state.wallet.address);
						const { address, } = get().wallet;
						if(address) {
							set(produce<Store>(state => {
								state.wallet.ready = true;
								state.wallet.resetting = false;
								state.wallet.modal_active = false;
							}));
						}
					})();
				}
				break;
			}

			case "CONNECT_ERROR": {
				//TODO: consume this better.
				console.warn("received a connect error event", JSON.parse(JSON.stringify(data)),);
				break;
			}

			case "MODAL_OPEN": {

				const { modal_active, } = get().wallet;
				if(!modal_active) {
					// console.log("WALLET >", event, JSON.parse(JSON.stringify(evt)),);
					set(produce<Store>(state => {
						state.wallet.modal_active = true;
					}));

				}
				break;

			}

			case "MODAL_CLOSE": {
				set(produce<Store>(state => { state.wallet.modal_active = false; }))
				break;
			}

			default:
				console.warn("unknown w3c modal event", event);
				console.log("EVENT", event, data);
		}
	}


	const _watchConnectedWallet = async () => {
		const { address, anonymous, } = get().wallet;
		if(!address) {
			throw new Error("WalletSlice:: no address found to watch");
		}
		if(walletSub) walletSub();
		if(!anonymous) {
			walletSub = await subscribe(`wallets.${address.toLowerCase()}`, __onWalletMessage);
		}
	};


	const __onWalletMessage = async ( message: unknown, ) => {
		if(message && typeof message === "object" && !Array.isArray(message) && message !== null && "type" in message) {
			const { type, } = message;
			if(type === "BRKTWALLET:KYC_UPDATE") {
				const wallet = await R.userData(get());
				set(produce<Store>(state => {
					state.wallet.user = wallet.user;
				}));
			}
		}
	};


	const _watch = () => {
		if(!watcher) watcher = watchAccount(config, { onChange: __onAccountChange, });
	};


	let watcher: ReturnType<typeof watchAccount>;
	let walletSub: Awaited<ReturnType<typeof subscribe>>;
	let _poll_listener: NodeJS.Timeout|null = null;
	let _listening = false;

	if(!_listening) {
		modal.subscribeEvents(__onModalEvent);
		_listening = true;
	}

	// place at the bottom of the call stack.
	setTimeout(_ => fetch(), 0);

	return {
		address: null,
		anonymous: true,
		balance: {
			usd: -1,
			brkt: "0",
		},
		brkt_balance: BigInt(0),
		brkt_conv_rate: 0,
		chain: null,
		chainId: null,
		checked_in: false,
		client: {
			readable: null,
			writable: null,
		},
		controller: new AbortController(),
		eth_balance: BigInt(0),
		eth_price: 0,
		fetching: false,
		isConnected: false,
		isDisconnected: true,
		isSupportedChain: false,
		modal_active: false,
		poll_listener: null,
		ready: false,
		trm_pass: true,
		whitelisted: false,
		user: null,
		country_code: null,
		kyc_level: 0,
		kyc_active: false,
		_modalSub: null,
		resetting: false,
		claim_amount: BigInt(0),
		has_claim: false,
		claiming: false,
		claim_proof: [],
		claimr_rank: null,
		claimr_points: null,
		brkt_transactions: null,

		fetch,
		agree,
		claimEscrow,
		cleanup,
		connect,
		disconnect,
		hasAgreed,
		reset,
		resetAsync,
		startKycDialogue: () => {},
	};

};
